import {
    PosMicrosIntegrationMicrosOutlet,
    PosMicrosIntegrationTransmissionProtocol,
} from '@mews-commander/data-access';
import { useDialogs } from '@mews-commander/ui';
import { LocalizedText, lt, useUrlSyncedTableState } from '@mews/framework';
import { Button, IconName, Table, TableHeader, TableProvider } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { UnicodeEntity } from '@mews/enums';
import { usePosMicrosConfigurationContext } from '../../contexts/posMicrosConfigurationContext';
import { ADD_MICROS_OUTLET_DIALOG_NAME } from '../constant';
import { MicrosOutletFormDialog } from './MicrosOutletFormDialog';
import { useMicrosOutletsColumns } from './useMicrosOutletsColumns';
import { useDetailedBillingConfigurationFlags } from './useDetailedBillingConfigurationFlags';

export interface MicrosOutletTableProps {
    protocol: PosMicrosIntegrationTransmissionProtocol;
}

export const MicrosOutletTable = ({ protocol }: MicrosOutletTableProps) => {
    const {
        posMicrosConfigurationState: { microsOutlets },
    } = usePosMicrosConfigurationContext();
    const { isDetailedBillingEnabled, isMultiDepartmentEnabled } = useDetailedBillingConfigurationFlags();

    const columns = useMicrosOutletsColumns(protocol);
    const [tableState, setTableState] = useUrlSyncedTableState<PosMicrosIntegrationMicrosOutlet>({
        columns,
        pageSize: 50,
    });

    const { openDialog } = useDialogs();

    const handleOpenAddOutletDialog = () => {
        openDialog(
            <MicrosOutletFormDialog
                isDetailedBillingEnabled={isDetailedBillingEnabled}
                isMultiDepartmentEnabled={isMultiDepartmentEnabled}
                protocol={protocol}
            />,
            ADD_MICROS_OUTLET_DIALOG_NAME
        );
    };

    return (
        <TableProvider columns={columns} state={tableState} onStateChange={setTableState} as="div">
            <TableHeader
                title={
                    <>
                        <LocalizedText textKey={TextKey.MicrosOutlets} />
                        <span>
                            {UnicodeEntity.Space}
                            {UnicodeEntity.LeftParenthesis}
                        </span>
                        <LocalizedText textKey={TextKey.RevenueCenters} />
                        <span>{UnicodeEntity.RightParenthesis}</span>
                    </>
                }
                rightSide={
                    <Button
                        type="button"
                        variant="tertiary"
                        icon={IconName.PlusSimple}
                        onClick={handleOpenAddOutletDialog}
                    >
                        {lt('AddMicrosOutlet')}
                    </Button>
                }
            />
            <Table
                uniqueRowId="menuNumber"
                data={microsOutlets}
                amountOfRows={microsOutlets?.length}
                fixedFirstColumn
            />
        </TableProvider>
    );
};
